// components/ChangePasswordModal.js
import React, { useState } from 'react';
import { FaKey, FaEye, FaEyeSlash, FaMapMarkerAlt, FaUpload, FaCheck, FaTimes } from 'react-icons/fa';

const ChangePasswordModal = ({ isOpen, onClose, onSuccess, userData }) => {
  const [step, setStep] = useState('password');
  const [addressUpdated, setAddressUpdated] = useState(null);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [newAddress, setNewAddress] = useState({
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    complemento: ''
  });
  const [comprovanteFile, setComprovanteFile] = useState(null);
  const [comprovanteUrl, setComprovanteUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const APP_URL = 'https://app.olympicbarbacena.com.br';

  const currentAddress = userData?.dadosAssociado;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleNewAddressChange = (e) => {
    setNewAddress({
      ...newAddress,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Apenas imagens (JPEG, PNG, GIF, WebP) ou PDF são permitidos');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('O arquivo deve ter no máximo 10MB');
      return;
    }

    setComprovanteFile(file);
    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${APP_URL}/api/upload/comprovante-endereco`, {
        method: 'POST',
        body: formDataUpload
      });

      const data = await response.json();

      if (response.ok) {
        setComprovanteUrl(data.url);
      } else {
        setError(data.error || 'Erro ao fazer upload do comprovante');
      }
    } catch (err) {
      setError('Erro de conexão ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (addressUpdated === null) {
      setError('Por favor, responda se seu endereço está atualizado');
      return;
    }

    if (addressUpdated === true) {
      if (!newAddress.logradouro || !newAddress.numero || !newAddress.bairro || !newAddress.cidade || !newAddress.uf || !newAddress.cep) {
        setError('Preencha todos os campos do novo endereço');
        return;
      }
      if (!comprovanteUrl) {
        setError('É necessário enviar o comprovante de residência');
        return;
      }
    }

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Todos os campos de senha são obrigatórios');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('As novas senhas não coincidem');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${APP_URL}/api/auth/change-password-first-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          newPasswordConfirm: formData.confirmPassword,
          addressUpdated: addressUpdated,
          newAddress: addressUpdated === true ? newAddress : null,
          comprovanteUrl: addressUpdated === true ? comprovanteUrl : null
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (data.needsAddressApproval) {
          alert('Senha alterada! Seu endereço foi enviado para validação. Você será notificado quando for aprovado.');
        }
        onSuccess();
        onClose();
      } else {
        setError(data.message || 'Erro ao alterar senha');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
      console.error('Error changing password:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('password');
    setAddressUpdated(null);
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setNewAddress({
      logradouro: '',
      numero: '',
      bairro: '',
      cidade: '',
      uf: '',
      cep: '',
      complemento: ''
    });
    setComprovanteFile(null);
    setComprovanteUrl('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const formatAddress = () => {
    if (!currentAddress) return 'Endereço não disponível';
    const parts = [
      currentAddress.LOGRADOURO,
      currentAddress.NUMERO,
      currentAddress.BAIRRO,
      currentAddress.CIDADE,
      currentAddress.UF,
      currentAddress.CEP
    ].filter(Boolean);
    return parts.join(', ') || 'Endereço não disponível';
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <FaKey style={styles.icon} />
          <h2 style={styles.title}>Primeiro Acesso</h2>
        </div>

        <div style={styles.message}>
          <p>Por segurança, você precisa alterar sua senha no primeiro acesso.</p>
        </div>

        {step === 'password' && (
          <div style={styles.addressSection}>
            <div style={styles.addressBox}>
              <div style={styles.addressHeader}>
                <FaMapMarkerAlt style={styles.addressIcon} />
                <span style={styles.addressLabel}>Seu endereço atual:</span>
              </div>
              <p style={styles.addressText}>{formatAddress()}</p>
            </div>

            <div style={styles.questionBox}>
              <p style={styles.questionText}>Seu endereço está atualizado?</p>
              <div style={styles.yesNoButtons}>
                <button
                  type="button"
                  onClick={() => setAddressUpdated(true)}
                  style={addressUpdated === true ? styles.noButtonActive : styles.noButton}
                >
                  <FaTimes style={styles.buttonIcon} /> Não
                </button>
                <button
                  type="button"
                  onClick={() => setAddressUpdated(false)}
                  style={addressUpdated === false ? styles.yesButtonActive : styles.yesButton}
                >
                  <FaCheck style={styles.buttonIcon} /> Sim
                </button>
              </div>
            </div>

            {addressUpdated === true && (
              <div style={styles.newAddressSection}>
                <p style={styles.newAddressTitle}>Novo Endereço:</p>

                <div style={styles.inputRow}>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>Rua/Logradouro:</label>
                    <input
                      type="text"
                      name="logradouro"
                      value={newAddress.logradouro}
                      onChange={handleNewAddressChange}
                      placeholder="Rua/Avenida"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>Número:</label>
                    <input
                      type="text"
                      name="numero"
                      value={newAddress.numero}
                      onChange={handleNewAddressChange}
                      placeholder="Número"
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.inputRow}>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>Bairro:</label>
                    <input
                      type="text"
                      name="bairro"
                      value={newAddress.bairro}
                      onChange={handleNewAddressChange}
                      placeholder="Bairro"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>Complemento:</label>
                    <input
                      type="text"
                      name="complemento"
                      value={newAddress.complemento}
                      onChange={handleNewAddressChange}
                      placeholder="Complemento"
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.inputRow}>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>Cidade:</label>
                    <input
                      type="text"
                      name="cidade"
                      value={newAddress.cidade}
                      onChange={handleNewAddressChange}
                      placeholder="Cidade"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>UF:</label>
                    <input
                      type="text"
                      name="uf"
                      value={newAddress.uf}
                      onChange={handleNewAddressChange}
                      placeholder="UF"
                      maxLength={2}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.inputGroupSmall}>
                    <label style={styles.label}>CEP:</label>
                    <input
                      type="text"
                      name="cep"
                      value={newAddress.cep}
                      onChange={handleNewAddressChange}
                      placeholder="CEP"
                      style={styles.input}
                    />
                  </div>
                </div>

                <div style={styles.uploadSection}>
                  <label style={styles.label}>Comprovante de Residência:</label>
                  <div style={styles.uploadBox}>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                      onChange={handleFileChange}
                      style={styles.fileInput}
                      id="comprovante-upload"
                    />
                    <label htmlFor="comprovante-upload" style={styles.uploadLabel}>
                      <FaUpload style={styles.uploadIcon} />
                      {uploading ? 'Enviando...' : comprovanteUrl ? 'Comprovante enviado!' : 'Selecionar arquivo'}
                    </label>
                    {comprovanteUrl && <span style={styles.uploadSuccess}>✓</span>}
                  </div>
                  <p style={styles.uploadHint}>Envie uma foto ou PDF do comprovante (máx. 10MB)</p>
                </div>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Senha Atual:</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Digite sua senha atual"
                style={styles.input}
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                style={styles.eyeButton}
              >
                {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Nova Senha:</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Digite a nova senha (mín. 8 caracteres)"
                style={styles.input}
                required
                minLength="8"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={styles.eyeButton}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirmar Nova Senha:</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirme a nova senha"
                style={styles.input}
                required
                minLength="8"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeButton}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.buttons}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={loading || uploading}
            >
              {loading ? 'Enviando...' : 'Continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '15px',
  },
  icon: {
    fontSize: '24px',
    color: '#2563EB',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  message: {
    backgroundColor: '#EFF6FF',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center',
    border: '1px solid #2563EB',
  },
  addressSection: {
    marginBottom: '20px',
  },
  addressBox: {
    backgroundColor: '#F3F4F6',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  addressHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  addressIcon: {
    color: '#6B7280',
    fontSize: '14px',
  },
  addressLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
  },
  addressText: {
    fontSize: '13px',
    color: '#4B5563',
    margin: 0,
    lineHeight: '1.4',
  },
  questionBox: {
    textAlign: 'center',
    marginBottom: '15px',
  },
  questionText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '10px',
  },
  yesNoButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
  },
  yesButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    border: '2px solid #10B981',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  yesButtonActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#10B981',
    color: 'white',
    border: '2px solid #10B981',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  noButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    border: '2px solid #EF4444',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  noButtonActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#EF4444',
    color: 'white',
    border: '2px solid #EF4444',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonIcon: {
    fontSize: '12px',
  },
  newAddressSection: {
    backgroundColor: '#FEF3C7',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #F59E0B',
  },
  newAddressTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#92400E',
    marginBottom: '12px',
  },
  inputRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  inputGroupSmall: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  uploadSection: {
    marginTop: '10px',
  },
  uploadBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  fileInput: {
    display: 'none',
  },
  uploadLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#F59E0B',
    color: 'white',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  uploadIcon: {
    fontSize: '14px',
  },
  uploadSuccess: {
    color: '#10B981',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  uploadHint: {
    fontSize: '12px',
    color: '#92400E',
    marginTop: '6px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 40px 10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '0',
  },
  error: {
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    padding: '10px',
    borderRadius: '8px',
    fontSize: '13px',
    textAlign: 'center',
    border: '1px solid #FECACA',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '90px',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '120px',
  },
};

export default ChangePasswordModal;